import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { MenuBook, Vaccines } from '@mui/icons-material'
import API from '../../../../services/API'

// CD register overview: every controlled drug with its live balance per
// location. Click a holding to open that register's ledger.
const CDRegister = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [drugs, setDrugs] = useState([])

  useEffect(() => {
    API.get('/medsmx/cd-register')
      .then((res) => {
        if (res.status === 200) {
          setDrugs(res.data.drugs)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  return (
    <Stack gap={1}>
      <Stack direction="row" gap={1} alignItems="center">
        <MenuBook color="primary" />
        <Typography variant="title2">Controlled Drug Register</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Live balances for every controlled drug, per bag and store. Balances
        are counted from stock records, never stored — open a holding to see
        its full ledger and export it for inspection.
      </Typography>
      {loading && <LinearProgress />}
      {fetchError && (
        <Alert severity="error">
          Unable to load the CD register. Please try again later.
        </Alert>
      )}
      {!loading && !fetchError && drugs.length === 0 && (
        <Alert severity="info">
          No controlled drugs are set up in your formulary. Mark a medicine as
          controlled in Site Configuration → Medicines to start its register.
        </Alert>
      )}
      {drugs.map((drug) => (
        <Stack component={Paper} key={drug._id} padding={1.5} gap={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Stack>
              <Typography variant="title3">{drug.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[drug.constitution, drug.presentation]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Stack>
            <Stack direction="row" gap={1} alignItems="center">
              {drug.preparedHeld > 0 && (
                <Chip
                  size="small"
                  icon={<Vaccines />}
                  label={`${drug.preparedHeld} drawn up & held`}
                  color="warning"
                  variant="outlined"
                />
              )}
              <Chip label={`Total in stock: ${drug.total}`} color="primary" />
            </Stack>
          </Stack>
          {drug.holdings.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No stock currently held.
            </Typography>
          ) : (
            <Box display="flex" gap={1} flexWrap="wrap">
              {drug.holdings.map((holding) => (
                <Chip
                  key={holding.location}
                  label={`${holding.name}: ${holding.qty}`}
                  variant="outlined"
                  onClick={() =>
                    navigate(
                      `/medicines-management/cd-register/${drug._id}?location=${holding.location}`,
                    )
                  }
                />
              ))}
            </Box>
          )}
        </Stack>
      ))}
    </Stack>
  )
}

export default CDRegister
